import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { useApi } from '../services/apiService';
import { ACCOUNT_TYPES } from '../utils/accountStatus';
import { parseAmount } from '../utils/format';

interface AddAccountDialogProps {
  currencies: string[];
  onClose: () => void;
  onSaved: () => void;
}

// Mounted only while open, so each opening starts with a blank form.
const AddAccountDialog: React.FC<AddAccountDialogProps> = ({
  currencies,
  onClose,
  onSaved,
}) => {
  const { createManualAccount } = useApi();
  const [name, setName] = useState('');
  const [type, setType] = useState(ACCOUNT_TYPES[0].value);
  const [currency, setCurrency] = useState('NZD');
  const [balanceInput, setBalanceInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balance = parseAmount(balanceInput);
  const nameInvalid = name.trim() === '';
  const currencyInvalid = !/^[A-Za-z]{3}$/.test(currency.trim());
  const balanceInvalid = balance === null;

  const handleConfirm = async () => {
    setSubmitted(true);
    if (nameInvalid || currencyInvalid || balance === null) return;
    setSaving(true);
    setError(null);
    try {
      await createManualAccount({
        name: name.trim(),
        type,
        currency: currency.trim().toUpperCase(),
        balance,
      });
      onSaved();
    } catch (err) {
      setError('Could not add the account. Please try again.');
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open
      title="Add manual account"
      confirmLabel="Add account"
      busy={saving}
      onConfirm={handleConfirm}
      onCancel={onClose}
    >
      <p className="muted">
        For accounts Akahu can't connect to. You update the balance yourself.
      </p>
      <label className="field">
        <span className="field-label">Account name</span>
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Term deposit – ANZ"
          aria-invalid={submitted && nameInvalid}
          maxLength={100}
        />
      </label>
      <label className="field">
        <span className="field-label">Type</span>
        <select
          className="input"
          value={type}
          onChange={(event) => setType(event.target.value)}
        >
          {ACCOUNT_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field-label">Currency</span>
          <input
            className="input"
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            list="account-currencies"
            maxLength={3}
            aria-invalid={submitted && currencyInvalid}
          />
          <datalist id="account-currencies">
            {currencies.map((code) => (
              <option key={code} value={code} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span className="field-label">Current balance</span>
          <input
            className="input num"
            value={balanceInput}
            onChange={(event) => setBalanceInput(event.target.value)}
            inputMode="decimal"
            placeholder="0.00"
            aria-invalid={submitted && balanceInvalid}
          />
        </label>
      </div>
      <p className="field-hint">
        Use a negative amount for loans and credit cards.
      </p>
      {error && <p className="error">{error}</p>}
    </ConfirmDialog>
  );
};

export default AddAccountDialog;
